import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { CoreModule } from "../../../core/core.module";
import { ModalService } from "@core-services/modal.service";
import { CoolOffNotificationModalComponent } from "@modules/uk-finprom/components/cool-off-notification-modal/cool-off-notification-modal.component";
import {
	COOL_OFF_TRADE_STATUSES,
	CoolOffWidgetModel
} from "@modules/uk-finprom/components/cool-off-widget/entities/cool-off-widget.model";
import { map, takeWhile } from "rxjs/operators";
import { EMPTY, Observable, timer } from "rxjs";

@Component({
	selector: "cool-off-widget",
	templateUrl: "./cool-off-widget.component.html",
	styleUrls: ["./cool-off-widget.component.scss"],
	standalone: true,
	imports: [CoreModule],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CoolOffWidgetComponent {
	protected readonly COOL_OFF_TRADE_STATUSES = COOL_OFF_TRADE_STATUSES;

	private readonly modalService = inject(ModalService);

	private _data: CoolOffWidgetModel;
	public endTime: number;
	public startTime: number;
	public modalData: CoolOffWidgetModel;
	public progress$: Observable<number> = EMPTY;

	@Input() set data(data: CoolOffWidgetModel) {
		const { end_time, start_time } = data;

		this.startTime = start_time ? new Date(start_time).getTime() : null;
		this.endTime = end_time ? new Date(end_time).getTime() : null;

		if (this.getTimeDifferenceFromNow(this.endTime) > 0) {
			this.progress$ = this.getTimer$(this.endTime);
		}

		this.modalData = data;
		this._data = data;
	}

	@Output() public countdownEnd = new EventEmitter<boolean>();

	public showNotificationModal(): void {
		this.modalService.create(CoolOffNotificationModalComponent, { data: this.modalData }).present();
	}

	get data(): CoolOffWidgetModel {
		return this._data;
	}

	public getTimer$(endtime: number): Observable<number> {
		return timer(0, 1000).pipe(
			map(() => {
				const timeDifference = this.getTimeDifferenceFromNow(endtime);

				// if countdown is complete
				if (timeDifference === 0) {
					this.countdownEnd.emit(true);
					return null;
				}

				// return progress bar value
				return this.progressCalculations(this.startTime, this.endTime);
			}),
			// unsubscribe when countdown is completed
			// "true" flag says to also emit the value that caused the condition to be false (we want to display the last 0 in the countdown)
			takeWhile((timeDifference) => timeDifference >= 0, true)
		);
	}

	private getTimeDifferenceFromNow(startTime: number): number {
		const diff = Math.round((startTime - new Date().getTime()) / 1000);
		return Math.max(0, diff);
	}

	private progressCalculations(createdTimeStamp: number, finalTimeStamp: number): number {
		const overallDuration = Math.round(finalTimeStamp - Number(createdTimeStamp)) / 1000;
		const durationFromNow = Math.ceil(Math.max(0, Math.fround(finalTimeStamp - Date.now()) / 1000));

		const dd = overallDuration - durationFromNow;
		return Math.ceil((dd / overallDuration) * 100);
	}
}
